import { useEffect, useMemo, useState } from "react";

import { useAgentPrompts, useUpdateAgentPrompt } from "@/api/agent-prompts";
import { ApiError } from "@/api/client";
import type { AgentPromptRow } from "@/api/types";
import { ErrorBanner } from "@/components/ErrorBanner";
import { RelativeTime } from "@/components/RelativeTime";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * `/prompts` — review + edit the DB-backed system prompts for every
 * agent. The orchestrator pins the active version into each new job's
 * dispatch payload (`jobs.prompts_snapshot`) so edits take effect on
 * the next dispatch without a redeploy.
 *
 * Layout: list of `(agent_type, prompt_key)` entries on the left, the
 * selected prompt's editor on the right. Save creates a new monotonic
 * version row. PR-a is intentionally minimal — version history + diff
 * + rollback land in PR-b ([#216 follow-up]). Plain `<textarea>` for
 * the editor is fine because the LLM consumes raw text; markdown
 * preview is nice-to-have not blocking.
 *
 * Last-write-wins, no ETags — same posture as the agent-config Phase 2
 * editor. The audit row written on save records the new version, the
 * editing admin's verified email, and the timestamp, which is enough
 * to reconstruct who-edited-what after the fact even before PR-b's
 * history UI lands.
 *
 * Unseeded rows (`version === 0`) render a "seed required" banner
 * pointing at `docs/agent-prompts.md`. The edit form stays disabled
 * for them — the orchestrator's PUT does accept v0→v1, but seeding via
 * `pnpm db:seed-agent-prompts` is the documented onboarding path so
 * the operator doesn't accidentally diverge from the `.md` source.
 */
export function Prompts() {
  const query = useAgentPrompts();
  const [selected, setSelected] = useState<string | null>(null);

  const rows = query.data?.rows ?? [];

  // Default selection: first row once the list arrives. `useEffect`
  // (not `useMemo`) because we only want to set this once on the
  // initial load.
  useEffect(() => {
    if (!selected && rows.length > 0) {
      const first = rows[0];
      if (first) setSelected(promptKey(first));
    }
  }, [rows, selected]);

  const activeRow = useMemo(
    () => rows.find((r) => promptKey(r) === selected) ?? null,
    [rows, selected],
  );

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-light tracking-tight">Prompts</h1>
        <p className="mt-2 text-sm text-text-muted max-w-3xl">
          Review and edit each agent&apos;s system prompt. Saving creates a
          new version; the next job dispatched picks it up automatically.
          See <span className="font-mono text-xs">docs/agent-prompts.md</span>{" "}
          for the workflow, rollback, and reproducibility story.
        </p>
      </header>

      {query.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : query.isError ? (
        <ErrorBanner error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <div className="grid grid-cols-[260px_1fr] gap-6">
          <PromptList
            rows={rows}
            selected={selected}
            onSelect={setSelected}
          />
          {activeRow ? (
            <PromptEditor row={activeRow} />
          ) : (
            <Card className="p-6">
              <p className="text-sm text-text-muted">
                Select a prompt on the left to edit.
              </p>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

function promptKey(row: AgentPromptRow): string {
  return `${row.agentType}/${row.promptKey}`;
}

interface PromptListProps {
  rows: AgentPromptRow[];
  selected: string | null;
  onSelect: (key: string) => void;
}

function PromptList({ rows, selected, onSelect }: PromptListProps) {
  return (
    <nav aria-label="Agent prompts" className="space-y-1">
      {rows.map((row) => {
        const key = promptKey(row);
        const isActive = key === selected;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onSelect(key)}
            className={
              "w-full text-left rounded px-3 py-2 transition-colors " +
              (isActive
                ? "bg-bg-card text-text"
                : "hover:bg-bg-card-hover text-text-muted")
            }
          >
            <div className="font-mono text-xs">{row.agentType}</div>
            <div className="mt-0.5 flex items-center gap-2">
              <span className="text-sm">{row.promptKey}</span>
              <Badge variant={row.version === 0 ? "muted" : "accent"}>
                {row.version === 0 ? "unseeded" : `v${row.version}`}
              </Badge>
            </div>
          </button>
        );
      })}
    </nav>
  );
}

interface PromptEditorProps {
  row: AgentPromptRow;
}

function PromptEditor({ row }: PromptEditorProps) {
  const update = useUpdateAgentPrompt();
  const [draft, setDraft] = useState(row.content);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Reset the draft whenever the operator switches prompts or a save
  // lands and the list refetches. Keying on `(agentType, promptKey,
  // version)` covers both cases: selection change moves agentType /
  // promptKey, save-completion bumps version.
  useEffect(() => {
    setDraft(row.content);
    setErrorMessage(null);
  }, [row.agentType, row.promptKey, row.version, row.content]);

  const dirty = draft !== row.content;
  const isUnseeded = row.version === 0;

  const onSave = async () => {
    setErrorMessage(null);
    try {
      await update.mutateAsync({
        agentType: row.agentType,
        promptKey: row.promptKey,
        content: draft,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; message?: string } | null;
        setErrorMessage(
          body?.message ??
            body?.error ??
            `Save failed (${err.status})`,
        );
      } else {
        setErrorMessage(err instanceof Error ? err.message : String(err));
      }
    }
  };

  return (
    <Card className="p-6 space-y-4">
      <header className="space-y-2">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-medium">
            <span className="font-mono text-sm text-text-muted">{row.agentType}</span>{" "}
            <span className="text-text-muted">/</span>{" "}
            {row.promptKey}
          </h2>
          <Badge variant={isUnseeded ? "muted" : "accent"}>
            {isUnseeded ? "unseeded" : `v${row.version}`}
          </Badge>
        </div>
        {!isUnseeded && row.createdAt ? (
          <p className="text-xs text-text-muted">
            Last edited{" "}
            <RelativeTime date={row.createdAt} />
            {row.createdBy ? ` by ${row.createdBy}` : ""}
          </p>
        ) : null}
        {isUnseeded ? (
          <p className="text-sm text-amber-500">
            This prompt has not been seeded. Run{" "}
            <span className="font-mono">pnpm db:seed-agent-prompts</span> from
            the platform repo (see{" "}
            <span className="font-mono text-xs">docs/agent-prompts.md</span>).
          </p>
        ) : null}
      </header>

      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        disabled={isUnseeded}
        className="w-full min-h-[480px] font-mono text-sm bg-bg-card text-text rounded border border-border p-3 focus:outline-none focus:ring-2 focus:ring-accent"
        spellCheck={false}
      />

      {errorMessage ? (
        <div className="rounded border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
          {errorMessage}
        </div>
      ) : null}

      <div className="flex items-center justify-between">
        <span className="text-xs text-text-muted">
          {dirty ? "Unsaved changes" : "Saved"}
        </span>
        <Button
          onClick={() => void onSave()}
          disabled={!dirty || isUnseeded || update.isPending}
        >
          {update.isPending ? "Saving…" : "Save new version"}
        </Button>
      </div>
    </Card>
  );
}
